import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IconCardView, IconListView, SearchOutlined } from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { Skeleton } from '../../components/ui/Skeleton'
import { useToast } from '../../components/ui/ToastProvider'
import { NewWorkspaceModal } from './components/NewWorkspaceModal'
import { WorkspaceCard } from './components/WorkspaceCard'
import { WorkspaceListRow } from './components/WorkspaceListRow'
import { defaultWorkspacesService, useWorkspaceList } from './useWorkspaces'
import type { WorkspacesService } from './useWorkspaces'

export interface AllWorkspacesPageProps {
  /** Injectable for tests / future BFF-backed swap. */
  readonly service?: WorkspacesService
}

type ViewMode = 'grid' | 'list'
const SKELETON_KEYS = ['a', 'b', 'c', 'd']

/**
 * All workspaces (UID-48b, screens.md §9) — browse/manage every workspace
 * the caller belongs to (per the frozen `GET /api/workspaces` contract,
 * private workspaces the caller isn't a member of simply aren't returned —
 * "request access to a workspace you can't yet see" isn't a flow this
 * screen supports; that only happens via a direct link, see WorkspacePage).
 */
export function AllWorkspacesPage({
  service = defaultWorkspacesService,
}: AllWorkspacesPageProps = {}) {
  const { state, refetch } = useWorkspaceList(service)
  const navigate = useNavigate()
  const { showToast } = useToast()

  const [search, setSearch] = useState('')
  const [view, setView] = useState<ViewMode>('list')
  const [newWorkspaceOpen, setNewWorkspaceOpen] = useState(false)

  const workspaces = state.status === 'ready' ? state.workspaces : []
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return workspaces
    return workspaces.filter((w) => w.name.toLowerCase().includes(q))
  }, [workspaces, search])

  return (
    <section aria-labelledby="all-workspaces-heading" className="all-workspaces-page">
      <div className="all-workspaces-header">
        <div className="all-workspaces-heading-col">
          <h1 id="all-workspaces-heading" className="display-heading">
            Projects
          </h1>
          {/* UID-370, comp `Fictiv Platform.dc.html:1600`. The count is the
           * UNFILTERED list: `wsAllCountLabel` (`:10452`) reads `_projList`
           * (`:8755`, the full `projects()`), while the search box only narrows
           * the cards (`wsAllCards`, `:8768`) — hence `workspaces`, not
           * `visible`. Gated on `ready` because `workspaces` is `[]` for every
           * other status, and "0 projects" printed above "We couldn't load your
           * projects" would assert a count we don't actually know. */}
          {state.status === 'ready' && (
            <p className="all-workspaces-subtitle">
              {`${workspaces.length} project${workspaces.length === 1 ? '' : 's'} · group parts, BOMs, quotes, and orders for a build or team.`}
            </p>
          )}
        </div>

        {/* UID-382, comp `Fictiv Platform.dc.html:1601-1610` — the right-hand
         * cluster. The comp puts the search box (`:1602-1605`), the icon-only
         * view-toggle pill (`:1606-1609`) and the "New project" button
         * (`:1610`) in ONE flex group inside the same `space-between` row as
         * the heading column, in that order. The repo had the search+toggle in
         * a separate `.all-workspaces-toolbar` row below the header, with the
         * button alone in the header row. Only the position and the toggle's
         * styling change here — the `ready` gating is UID-371's and is
         * preserved verbatim (see below), and the empty-state branching further
         * down is untouched. */}
        <div className="all-workspaces-actions">
          {/* UID-371 (unchanged by UID-382) — comp
           * `Fictiv Platform.dc.html:1599-1612` does NOT gate the search box or
           * the toggle on the collection being non-empty; the empty state
           * (`:1614`, shown when `wsAllEmpty` = `wsAllCards.length===0`,
           * `:10453`) is a *sibling* below the row. So this is gated on `ready`
           * ONLY: `workspaces.length` must not hide it. No `disabled` handling
           * is needed for the empty case — `visible` is a useMemo over
           * `workspaces`, so an empty list filters as `[].filter(...)` (inert),
           * and the toggle only flips local `view` state. Still gated on
           * `ready` because loading/error have no list to filter and these
           * controls would imply otherwise. */}
          {state.status === 'ready' && (
            <>
              {/* Comp `:1602-1605` — the input is wrapped so the search glyph
               * can be absolutely positioned over its 36px left padding.
               * Modelled on `.staging-search`/`.staging-search-icon` from
               * `StagingToolbar.tsx` — deleted by UID-402 (ADR-0124 D6) along
               * with the Triage search box, and reinstated by ADR-0155. Never
               * a shared component; only a shared shape. */}
              <div className="workspaces-search">
                <label className="sr-only" htmlFor="workspaces-search">
                  Search projects
                </label>
                <SearchOutlined aria-hidden="true" className="workspaces-search-icon" />
                <input
                  id="workspaces-search"
                  type="search"
                  className="workspaces-search-input"
                  placeholder="Search projects"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>

              {/* Comp `:1606-1609` — icon-only segmented pill. Kept as a
               * `<fieldset>` + sr-only `<legend>` (the pre-UID-382 markup's
               * grouping) rather than the comp's bare `<div>`/`<span onClick>`,
               * and as real `<button type="button" aria-pressed>` elements:
               * the same accessibility posture this codebase takes everywhere
               * it diverges from the comp's raw HTML. New classes rather than
               * restyling `.view-toggle-*` — those are shared with
               * CatalogSeriesPage.tsx:121, which keeps the text-label style. */}
              <fieldset className="workspaces-view-toggle">
                <legend className="sr-only">View</legend>
                <button
                  type="button"
                  aria-label="Card view"
                  aria-pressed={view === 'grid'}
                  className={
                    view === 'grid'
                      ? 'workspaces-view-toggle-btn workspaces-view-toggle-btn-active'
                      : 'workspaces-view-toggle-btn'
                  }
                  onClick={() => setView('grid')}
                >
                  <IconCardView />
                </button>
                <button
                  type="button"
                  aria-label="List view"
                  aria-pressed={view === 'list'}
                  className={
                    view === 'list'
                      ? 'workspaces-view-toggle-btn workspaces-view-toggle-btn-active'
                      : 'workspaces-view-toggle-btn'
                  }
                  onClick={() => setView('list')}
                >
                  <IconListView />
                </button>
              </fieldset>
            </>
          )}

          {/* Comp `:1610`. Ungated (unlike the search/toggle above) — it is the
           * only create affordance while loading or on error. */}
          <Button onClick={() => setNewWorkspaceOpen(true)}>New project</Button>
        </div>
      </div>

      {state.status === 'loading' && (
        <div aria-busy="true" aria-label="Loading projects" className="all-workspaces-skeleton">
          {SKELETON_KEYS.map((key) => (
            <Skeleton key={key} height={96} />
          ))}
        </div>
      )}

      {state.status === 'error' && (
        <EmptyState
          title="We couldn’t load your projects"
          detail={state.message}
          action={<Button onClick={refetch}>Try again</Button>}
        />
      )}

      {state.status === 'ready' && (
        <>
          {/* Deliberate divergence from the comp, which collapses both empty
           * cases into one generic CTA-less "No projects match your search."
           * (`:1615`) and leans on the header's "New project" button (comp
           * `:1610`; ours is in `.all-workspaces-actions` above, and is
           * deliberately ungated) as the only create affordance. To be
           * precise about the trade-off: converging would NOT strand a
           * zero-project user with no way to create one — the header button is
           * always there. Keeping the two messages distinct is a
           * discoverability/accuracy call, not a lost-affordance one: it names
           * why the list is empty rather than claiming a filter excluded
           * everything on a list that was never filtered, and puts the next
           * action in the user's line of sight on an otherwise-bare page.
           * Convergence is therefore cheap if design ever asks — out of scope
           * for this visibility fix. */}
          {workspaces.length === 0 && (
            <EmptyState
              title="You don’t belong to any projects yet"
              action={<Button onClick={() => setNewWorkspaceOpen(true)}>New project</Button>}
            />
          )}

          {workspaces.length > 0 &&
            (visible.length === 0 ? (
              <EmptyState title="No projects match your search" />
            ) : (
              <div className={view === 'grid' ? 'all-workspaces-grid' : 'all-workspaces-list'}>
                {/* UID-373 — the two views render DIFFERENT components. Until
                 * now both rendered `WorkspaceCard`, which is hardcoded
                 * `flex-direction: column` (workspaces.css), so "List view"
                 * was the same vertical card stacked repeatedly and nothing
                 * lined up into row-columns. The comp specifies a dedicated
                 * horizontal row for List (`Fictiv Platform.dc.html:1647-1668`)
                 * alongside the card for Grid (`:1564-1590`) — hence
                 * `WorkspaceListRow`. Grid is unchanged. */}
                {visible.map((workspace) =>
                  view === 'grid' ? (
                    <WorkspaceCard
                      key={workspace.id}
                      workspace={workspace}
                      onOpen={(id) => navigate(`/projects/${id}`)}
                    />
                  ) : (
                    <WorkspaceListRow
                      key={workspace.id}
                      workspace={workspace}
                      onOpen={(id) => navigate(`/projects/${id}`)}
                    />
                  ),
                )}
              </div>
            ))}
        </>
      )}

      <NewWorkspaceModal
        open={newWorkspaceOpen}
        onClose={() => setNewWorkspaceOpen(false)}
        onCreate={async (request) => {
          try {
            const created = await service.createWorkspace(request)
            refetch()
            navigate(`/projects/${created.id}`)
          } catch (error) {
            showToast(
              error instanceof Error ? error.message : 'Could not create the project. Try again.',
              'error',
            )
            // Rethrow so NewWorkspaceModal's own catch (which swallows and
            // keeps the modal open for a retry) sees the failure, instead
            // of it treating this as a successful create and closing.
            throw error
          }
        }}
      />
    </section>
  )
}
